import React from 'react';
import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
} from '@mui/material';

/**
 * Generic hero/banner card composed of optional badges, headline copy,
 * calls-to-action, and a right-hand content area. Keeps dashboard hero
 * implementations consistent across admin surfaces.
 */
const HeroCard = ({
  title,
  subtitle,
  description,
  badges = [],
  actions = [],
  rightContent = null,
  sx,
  contentSx,
  actionLayout = 'row',
  ...rest
}) => (
  <Card
    sx={{
      p: { xs: 4, md: 5 },
      borderRadius: '12px !important',
      background: (theme) => `linear-gradient(125deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 65%)`,
      color: 'primary.contrastText',
      boxShadow: '0 8px 20px rgba(56, 34, 94, 0.18)',
      overflow: 'visible',
      ...sx,
    }}
    {...rest}
  >
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      spacing={{ xs: 3, lg: 4 }}
      alignItems={{ xs: 'flex-start', lg: 'stretch' }}
      sx={contentSx}
    >
      <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
        {(badges.length > 0 || subtitle) && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            {badges.map(({ label, icon, key = label, borderColor, background, color }) => (
              <Box
                key={key}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.75,
                  py: 0.7,
                  borderRadius: 1,
                  border: borderColor ? `1px solid ${borderColor}` : '1px solid rgba(255,255,255,0.22)',
                  bgcolor: background || 'rgba(255,255,255,0.12)',
                  color: color || 'inherit',
                  fontWeight: 600,
                }}
              >
                {icon}
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {label}
                </Typography>
              </Box>
            ))}
            {subtitle && (
              <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                {subtitle}
              </Typography>
            )}
          </Stack>
        )}

        {title && (
          <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.6px' }}>
            {title}
          </Typography>
        )}

        {description && (
          <Typography sx={{ opacity: 0.9, maxWidth: 640, fontSize: 18, lineHeight: 1.6 }}>
            {description}
          </Typography>
        )}

        {actions.length > 0 && (
          <Stack
            direction={{ xs: 'column', sm: actionLayout }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          >
            {actions.map(
              (
                {
                  label,
                  onClick,
                  icon,
                  color = 'primary',
                  variant = 'contained',
                  key = label,
                  ...buttonProps
                },
              ) => (
                <Button
                  key={key}
                  onClick={onClick}
                  color={color}
                  variant={variant}
                  startIcon={icon}
                  sx={{
                    minWidth: 180,
                    px: 3,
                    py: 1.25,
                    borderRadius: 999,
                  }}
                  {...buttonProps}
                >
                  {label}
                </Button>
              ),
            )}
          </Stack>
        )}
      </Stack>

      {rightContent && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', lg: 'flex-end' },
            minWidth: { lg: 240 },
          }}
        >
          {rightContent}
        </Box>
      )}
    </Stack>
  </Card>
);

export default HeroCard;
